import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, ArrowLeft, Upload } from "lucide-react";
import { toast } from "sonner";
const emptyEmp = {
  name: "", employee_code: "", father_name: "", aadhaar: "", pan: "", mobile: "", email: "", address: "",
  bank_name: "", account_number: "", ifsc: "", upi_id: "",
  joining_date: "", department: "",
  salary_type: "daily", daily_wage: 0, monthly_salary: 0, overtime_rate: 0,
  emergency_contact_name: "", emergency_contact_phone: "",
  status: "active", photo_url: null,
};

export default function EmployeeForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const nav = useNavigate();
  const [emp, setEmp] = useState(emptyEmp);
  const [loading, setLoading] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (editing) {
      (async () => {
        try {
          const { data } = await api.get(`/employees/${id}`);
          setEmp({ ...emptyEmp, ...data });
        } catch { toast.error("Could not load employee"); }
      })();
    }
  }, [id, editing]);

  const set = (k, v) => setEmp((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!emp.name.trim()) return toast.error("Name is required");
    setLoading(true);
    try {
      const payload = {
        ...emp,
        daily_wage: Number(emp.daily_wage) || 0,
        monthly_salary: Number(emp.monthly_salary) || 0,
        overtime_rate: Number(emp.overtime_rate) || 0,
      };
      if (editing) {
        await api.put(`/employees/${id}`, payload);
        toast.success("Employee updated");
      } else {
        const { data } = await api.post("/employees", payload);
        toast.success("Employee saved");
        nav(`/admin/employees/${data.id}/edit`);
        return;
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!id) return toast.error("Save the employee first, then add a photo");
    const fd = new FormData();
    fd.append("file", file);
    setSavingPhoto(true);
    try {
      const { data } = await api.post(`/employees/${id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_url", data.photo_url);
      toast.success("Photo updated");
    } catch { toast.error("Upload failed"); }
    finally { setSavingPhoto(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="employee-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/admin/employees")} data-testid="back-to-employees">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold">{editing ? "Edit" : "New"} Profile</div>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">{editing ? emp.name || "Employee" : "Add Employee"}</h1>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card className="border-[#E5E2DC] p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-lg bg-slate-100 border border-[#E5E2DC] overflow-hidden flex items-center justify-center">
                {emp.photo_url ? (
                  <img src={`${BACKEND_URL}${emp.photo_url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-xs text-slate-400 text-center px-2">No photo</div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
                data-testid="photo-file-input"
              />
              <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => fileRef.current?.click()} disabled={savingPhoto || !editing} data-testid="upload-photo-button">
                <Upload className="w-3 h-3 mr-1" /> {savingPhoto ? "Uploading..." : "Upload Photo"}
              </Button>
              {!editing && <div className="text-[10px] text-slate-400 mt-1 text-center">Save first to add photo</div>}
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Employee Code *" testid="emp-code">
                <Input value={emp.employee_code || ""} onChange={(e) => set("employee_code", e.target.value.toUpperCase())} required placeholder="e.g. MRS001" data-testid="emp-code-input" />
              </Field>
              <Field label="Full Name *" testid="emp-name">
                <Input value={emp.name} onChange={(e) => set("name", e.target.value)} required data-testid="emp-name-input" />
              </Field>
              <Field label="Mobile Number">
                <Input value={emp.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 98765 43210" data-testid="emp-mobile-input" />
              </Field>
              <Field label="Email">
                <Input type="email" value={emp.email} onChange={(e) => set("email", e.target.value)} data-testid="emp-email-input" />
              </Field>
              <Field label="Father's Name">
                <Input value={emp.father_name || ""} onChange={(e) => set("father_name", e.target.value)} placeholder="e.g. Rahim Ali" data-testid="emp-father-input" />
              </Field>
              <Field label="Joining Date">
                <Input type="date" value={emp.joining_date || ""} onChange={(e) => set("joining_date", e.target.value)} data-testid="emp-joining-input" />
              </Field>
              <Field label="Department">
                <Input value={emp.department || ""} onChange={(e) => set("department", e.target.value)} placeholder="e.g. Production" data-testid="emp-department-input" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Address">
                  <Textarea value={emp.address} onChange={(e) => set("address", e.target.value)} rows={2} data-testid="emp-address-input" />
                </Field>
              </div>
            </div>
          </div>
        </Card>

        <Card className="border-[#E5E2DC] p-6">
          <SectionTitle>KYC & Identification</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Aadhaar Number">
              <Input value={emp.aadhaar} onChange={(e) => set("aadhaar", e.target.value)} maxLength={14} placeholder="XXXX XXXX XXXX" data-testid="emp-aadhaar-input" />
            </Field>
            <Field label="PAN Number">
              <Input value={emp.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} maxLength={10} placeholder="ABCDE1234F" data-testid="emp-pan-input" />
            </Field>
          </div>
        </Card>

        <Card className="border-[#E5E2DC] p-6">
          <SectionTitle>Bank & Payment Details</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Bank Name"><Input value={emp.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="emp-bank-input" /></Field>
            <Field label="Account Number"><Input value={emp.account_number} onChange={(e) => set("account_number", e.target.value)} data-testid="emp-account-input" /></Field>
            <Field label="IFSC Code"><Input value={emp.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase())} placeholder="SBIN0001234" data-testid="emp-ifsc-input" /></Field>
            <Field label="UPI ID (Optional)"><Input value={emp.upi_id} onChange={(e) => set("upi_id", e.target.value)} placeholder="name@upi" data-testid="emp-upi-input" /></Field>
          </div>
        </Card>

        <Card className="border-[#E5E2DC] p-6">
          <SectionTitle>Salary Configuration</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Salary Type">
              <Select value={emp.salary_type} onValueChange={(v) => set("salary_type", v)}>
                <SelectTrigger data-testid="emp-salary-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily Wage</SelectItem>
                  <SelectItem value="monthly">Monthly Salary</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Daily Wage (₹)">
              <Input type="number" min="0" step="0.01" value={emp.daily_wage} onChange={(e) => set("daily_wage", e.target.value)} disabled={emp.salary_type !== "daily"} data-testid="emp-daily-wage-input" />
            </Field>
            <Field label="Monthly Salary (₹)">
              <Input type="number" min="0" step="0.01" value={emp.monthly_salary} onChange={(e) => set("monthly_salary", e.target.value)} disabled={emp.salary_type !== "monthly"} data-testid="emp-monthly-salary-input" />
            </Field>
            <Field label="Overtime Rate (₹/hour)">
              <Input type="number" min="0" step="0.01" value={emp.overtime_rate} onChange={(e) => set("overtime_rate", e.target.value)} data-testid="emp-ot-rate-input" />
            </Field>
            <Field label="Status">
              <Select value={emp.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="emp-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Card>

        <Card className="border-[#E5E2DC] p-6">
          <SectionTitle>Emergency Contact</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Contact Name"><Input value={emp.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} data-testid="emp-ec-name-input" /></Field>
            <Field label="Contact Phone"><Input value={emp.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} data-testid="emp-ec-phone-input" /></Field>
          </div>
        </Card>

        {editing && (
          <Card className="border-[#E5E2DC] p-6">
            <SectionTitle>Documents</SectionTitle>
            <p className="text-xs text-slate-500 mb-4">Upload Aadhaar, PAN and Bank Passbook (Images or PDF up to 10MB).</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <DocumentField id={id} kind="aadhaar" label="Aadhaar Card" emp={emp} onUpdated={setEmp} />
              <DocumentField id={id} kind="pan" label="PAN Card" emp={emp} onUpdated={setEmp} />
              <DocumentField id={id} kind="passbook" label="Bank Passbook" emp={emp} onUpdated={setEmp} />
            </div>
          </Card>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => nav("/admin/employees")}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-employee-button" className="bg-[#111111] hover:bg-[#C59B27] transition-colors text-white">
            <Save className="w-4 h-4 mr-2" /> {loading ? "Saving..." : "Save Employee"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</Label>
    {children}
  </div>
);
const SectionTitle = ({ children }) => (
  <h3 className="font-serif text-xl text-slate-900 mb-4">{children}</h3>
);

function DocumentField({ id, kind, label, emp, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const doc = emp.documents?.[kind];
  const inputRef = useRef(null);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post(`/employees/${id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated((p) => ({ ...p, documents: { ...(p.documents || {}), [kind]: { url: data.url, filename: file.name } } }));
      toast.success(`${label} uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  const remove = async () => {
    try {
      await api.delete(`/employees/${id}/document/${kind}`);
      onUpdated((p) => {
        const docs = { ...(p.documents || {}) };
        delete docs[kind];
        return { ...p, documents: docs };
      });
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="border border-[#E5E2DC] rounded-md p-3 bg-white">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">{label}</div>
      {doc ? (
        <div className="space-y-2">
          {doc.url?.toLowerCase().endsWith(".pdf") ? (
            <div className="h-24 flex items-center justify-center bg-slate-50 rounded text-xs text-slate-500">PDF Document</div>
          ) : (
            <img src={`${BACKEND_URL}${doc.url}`} alt={label} className="w-full h-24 object-cover rounded" />
          )}
          <div className="flex gap-2">
            <a href={`${BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer" className="text-xs text-[#C59B27] hover:underline flex-1 text-center" data-testid={`view-doc-${kind}`}>View</a>
            <button type="button" onClick={remove} className="text-xs text-red-600 hover:underline flex-1" data-testid={`remove-doc-${kind}`}>Remove</button>
          </div>
        </div>
      ) : (
        <div className="h-24 flex items-center justify-center bg-slate-50 rounded text-xs text-slate-400">Not uploaded</div>
      )}
      <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid={`file-${kind}`} />
      <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} className="w-full mt-2" data-testid={`upload-doc-${kind}`}>
        <Upload className="w-3 h-3 mr-1" /> {uploading ? "Uploading..." : (doc ? "Replace" : "Upload")}
      </Button>
    </div>
  );
}
